import axios from 'axios';
import { authService } from './authService';

const PHOTO_SERVICE_BASE_URL = import.meta.env.VITE_PHOTO_SERVICE_URL || 'https://photo.agendario.com.br';
const UPLOAD_API_BASE_URL = import.meta.env.VITE_UPLOAD_API_URL || 'https://upload.agendario.com.br';

// Instância axios para o PhotoService (fotos de profissionais)
const photoApi = axios.create({
  baseURL: PHOTO_SERVICE_BASE_URL,
  headers: { 'Content-Type': 'multipart/form-data' },
});

photoApi.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Instância axios para o FileService (imagens de entidades: services, pages, etc.)
const uploadApi = axios.create({
  baseURL: UPLOAD_API_BASE_URL,
  headers: { 'Content-Type': 'multipart/form-data' },
});

uploadApi.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Cache-busting via localStorage ──────────────────────────────────────────
const PHOTO_TIMESTAMPS_KEY = 'professional_photo_timestamps';
const SERVICE_PHOTO_TIMESTAMPS_KEY = 'service_photo_timestamps';

function getPhotoTimestamps() {
  try {
    const stored = localStorage.getItem(PHOTO_TIMESTAMPS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function getPhotoTimestamp(professionalId) {
  return getPhotoTimestamps()[professionalId] || null;
}

function savePhotoTimestamp(professionalId, timestamp) {
  const timestamps = getPhotoTimestamps();
  timestamps[professionalId] = timestamp;
  localStorage.setItem(PHOTO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
}

function getServicePhotoTimestamps() {
  try {
    const stored = localStorage.getItem(SERVICE_PHOTO_TIMESTAMPS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function getServicePhotoTimestamp(serviceId) {
  return getServicePhotoTimestamps()[serviceId] || null;
}

function saveServicePhotoTimestamp(serviceId, timestamp) {
  const timestamps = getServicePhotoTimestamps();
  timestamps[serviceId] = timestamp;
  localStorage.setItem(SERVICE_PHOTO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
}

/**
 * Serviço de integração com PhotoService API
 * Upload, obtenção e deleção de fotos de perfil
 */
const photoService = {
  /**
   * Faz upload de foto de perfil do profissional
   * @param {File} file - Arquivo de imagem (já cropado)
   * @param {string} professionalId - ID do profissional
   * @returns {Promise<Object>} - { photoId, photoUrl, thumbnailUrl, originalFileSize, processedFileSize, thumbnailFileSize, dimensions, uploadedAt }
   */
  async uploadProfilePhoto(file, professionalId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('professionalId', professionalId);
    formData.append('generateThumbnail', 'false'); // Não precisamos de thumbnail
    formData.append('quality', '85');
    formData.append('maxSize', '800');

    const response = await photoApi.post('/api/photo/upload', formData);

    return response.data;
  },

  /**
   * Valida arquivo de imagem sem fazer upload
   * @param {File} file - Arquivo a validar
   * @returns {Promise<Object>} - { isValid, fileName, size, sizeKB, contentType, maxSizeMB }
   */
  async validateImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await photoApi.post('/api/photo/validate', formData);

    return response.data;
  },

  /**
   * Gera URL completa da foto de perfil do profissional (com cache-busting)
   * @param {string} userId - ID do usuário (namespace)
   * @param {string} professionalId - ID do profissional
   * @returns {string} - URL da foto com ?v=timestamp se houver timestamp salvo
   */
  getProfilePhotoUrl(userId, professionalId) {
    const photoCdnUrl = import.meta.env.VITE_PHOTO_CDN_URL || 'https://photos.agendario.com.br';
    const baseUrl = `${photoCdnUrl}/${userId}/${professionalId}.jpg`;
    const ts = getPhotoTimestamp(professionalId);
    return ts ? `${baseUrl}?v=${ts}` : baseUrl;
  },

  getPhotoTimestamp,
  savePhotoTimestamp,

  /**
   * Faz upload de imagem de um serviço via FileService.
   * POST /api/images com entityType=service
   * Path no bucket (Feature 104): {userId}/service/{serviceId}.jpg — userId vem do token no FileService.
   * CDN: images.agendario.com.br/{userId}/service/{serviceId}.jpg
   * @param {File} file - Arquivo de imagem (já cropado)
   * @param {string} serviceId - ID do serviço
   * @returns {Promise<Object>} - { fileId, fileUrl, ... }
   */
  async uploadServicePhoto(file, serviceId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', serviceId);
    formData.append('entityType', 'service');

    const response = await uploadApi.post('/api/images', formData);
    return response.data;
  },

  /**
   * Gera URL da imagem de um serviço (CDN com cache-busting)
   * CDN (Feature 104): images.agendario.com.br/{userId}/service/{serviceId}.jpg
   * @param {string} userId - ID do usuário dono do serviço (ServiceDto.userId)
   * @param {string} serviceId - ID do serviço
   */
  getServicePhotoUrl(userId, serviceId) {
    const imagesCdnUrl = import.meta.env.VITE_IMAGES_CDN_URL || 'https://images.agendario.com.br';
    const baseUrl = `${imagesCdnUrl}/${userId}/service/${serviceId}.jpg`;
    const ts = getServicePhotoTimestamp(serviceId);
    return ts ? `${baseUrl}?v=${ts}` : baseUrl;
  },

  getServicePhotoTimestamp,
  saveServicePhotoTimestamp,

  /**
   * Faz upload de imagem embutida em resposta de ticket de suporte via FileService.
   * POST /api/images com entityType=ticket_reply, entityId=GUID novo (mesma convenção do Dashboard).
   * Path no bucket: {userId}/ticket_reply/{guid}.{ext} — userId vem do token no FileService.
   * @param {File} file - Arquivo de imagem
   * @returns {Promise<Object>} - { fileUrl, ... }
   */
  async uploadTicketImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', crypto.randomUUID());
    formData.append('entityType', 'ticket_reply');

    const response = await uploadApi.post('/api/images', formData);
    return response.data;
  },

  /**
   * Faz upload de um anexo de nota de Person via FileService (type dedicado).
   * POST /api/files com fileType=notes, entityType={ownerUserId}/person-note, entityId={noteId}.
   * Path no bucket agendario-notes: {ownerUserId}/person-note/{noteId}/{filename}
   * → CDN notes.agendario.com.br/...
   * O owner é re-validado na API no registro (cliente não é autoridade).
   * @param {File} file - Arquivo (PDF ou imagem, <= 10MB)
   * @param {string} noteId - ID da nota
   * @param {string} ownerUserId - ID do dono do person (person.userId)
   * @returns {Promise<Object>} - { fileId, fileUrl, originalFileName, originalFileSize, processedFileSize, contentType, uploadedAt }
   */
  async uploadNoteAttachment(file, noteId, ownerUserId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', noteId);
    formData.append('entityType', `${ownerUserId}/person-note`);
    formData.append('fileType', 'notes');

    const response = await uploadApi.post('/api/files', formData);
    return response.data;
  },
};

export default photoService;
